import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LocateFixed, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";

export const STATUS_META = {
  loaded: { label: "Loaded & Running", hi: "माल लदा है, रास्ते में", cls: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/50 dark:text-emerald-300" },
  empty: { label: "Empty / Returning", hi: "खाली जा रही / रिटर्न", cls: "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-950/50 dark:text-sky-300" },
  waiting: { label: "Waiting for Load", hi: "लोडिंग के लिए खड़ी", cls: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/50 dark:text-amber-300" },
  breakdown: { label: "Breakdown / Workshop", hi: "खराब / वर्कशॉप में", cls: "bg-red-100 text-red-700 border-red-300 dark:bg-red-950/50 dark:text-red-300" },
};

const HIST_LABEL = { location: "Location update", breakdown: "Breakdown reported", resolved: "Breakdown resolved" };

export const mapUrl = (r) => (r.lat != null ? `https://www.google.com/maps?q=${r.lat},${r.lng}` : r.location ? `https://www.google.com/maps/search/${encodeURIComponent(r.location)}` : "");

export const ago = (iso) => {
  if (!iso) return "";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "abhi";
  if (m < 60) return `${m} min pehle`;
  if (m < 1440) return `${Math.round(m / 60)} ghante pehle`;
  return `${Math.round(m / 1440)} din pehle`;
};

function useGps(setF) {
  const [busy, setBusy] = useState(false);
  const get = () => {
    if (!navigator.geolocation) return toast.error("Is phone/browser me GPS nahi hai");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setBusy(false); setF((f) => ({ ...f, lat: +pos.coords.latitude.toFixed(6), lng: +pos.coords.longitude.toFixed(6) })); toast.success("GPS location mil gayi"); },
      () => { setBusy(false); toast.error("GPS permission nahi mili — location likh dein"); },
      { enableHighAccuracy: true, timeout: 15000 });
  };
  return [busy, get];
}

function GpsRow({ f, setF }) {
  const [busy, get] = useGps(setF);
  return (
    <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
      <Button type="button" variant="outline" size="sm" data-testid="live-gps-btn" disabled={busy} onClick={get}><LocateFixed className="mr-1 h-4 w-4" />{busy ? "Dhoond rahe…" : "Use Phone GPS"}</Button>
      {f.lat != null && <span className="font-mono text-xs text-muted-foreground" data-testid="live-gps-coords">{f.lat}, {f.lng}
        <button type="button" className="ml-2 text-destructive underline" data-testid="live-gps-clear" onClick={() => setF({ ...f, lat: null, lng: null })}>clear</button></span>}
    </div>
  );
}

function History({ refId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/tracking/history/${refId}`).then((r) => setRows(r.data)).catch(() => {}); }, [refId]);
  if (!rows.length) return null;
  return (
    <div className="max-h-36 overflow-y-auto rounded-lg border p-2 text-xs" data-testid="live-history">
      <div className="mb-1 font-semibold">Recent updates</div>
      {rows.slice(0, 10).map((h) => <div key={h.id} className="flex justify-between gap-2 border-b py-1 last:border-0">
        <span>{STATUS_META[h.status]?.label || HIST_LABEL[h.status] || h.status}{h.location ? ` · ${h.location}` : ""}{h.note ? ` · ${h.note}` : ""}</span>
        <span className="shrink-0 text-muted-foreground">{ago(h.updated_at)} · {h.updated_by}</span></div>)}
    </div>
  );
}

export function OwnUpdateDialog({ row, onClose, onSaved }) {
  const [f, setF] = useState({ status: row.status, location: row.location || "", lat: row.lat ?? null, lng: row.lng ?? null, note: "" });
  const save = async () => {
    try { await api.post(`/tracking/vehicles/${row.vehicle_id}`, f); toast.success("Status updated"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto bg-card" data-testid="own-update-dialog">
        <DialogHeader><DialogTitle>Update — {row.vehicle_number}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(STATUS_META).map(([k, m]) => (
            <button key={k} type="button" data-testid={`live-status-${k}`} onClick={() => setF({ ...f, status: k })}
              className={`rounded-lg border-2 p-2 text-left text-sm transition-transform active:scale-95 ${f.status === k ? `${m.cls} border-current` : "border-border hover:bg-accent"}`}>
              <div className="font-semibold">{m.label}</div><div className="text-[11px] opacity-80">{m.hi}</div></button>))}
        </div>
        {row.trip && <p className="text-xs text-muted-foreground">LR {row.trip.lr_number} chal raha hai — yahan badla status agle LR update tak dikhega.</p>}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Current Location" className="sm:col-span-2"><Input data-testid="live-location-input" placeholder="Jaise: Nagpur toll plaza" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></Field>
          <GpsRow f={f} setF={setF} />
          <Field label="Note" className="sm:col-span-2"><Input data-testid="live-note-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
        </div>
        <History refId={row.vehicle_id} />
        <DialogFooter><Button data-testid="live-save-btn" onClick={save}>Save Update</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MarketLocationDialog({ row, onClose, onSaved }) {
  const [f, setF] = useState({ location: row.location || "", lat: row.lat ?? null, lng: row.lng ?? null, note: "" });
  const save = async () => {
    try { await api.post(`/tracking/market/${row.trip_id}/location`, f); toast.success("Location updated"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-card" data-testid="market-location-dialog">
        <DialogHeader><DialogTitle><MapPin className="mr-1 inline h-4 w-4" />Location — {row.vehicle_number}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Current Location" className="sm:col-span-2"><Input data-testid="mkt-location-input" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></Field>
          <GpsRow f={f} setF={setF} />
          <Field label="Note" className="sm:col-span-2"><Input data-testid="mkt-note-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
        </div>
        <History refId={row.trip_id} />
        <DialogFooter><Button data-testid="mkt-location-save-btn" onClick={save}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const nowLocal = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };

export function BreakdownDialog({ row, onClose, onSaved }) {
  const b = row.breakdown;
  const [f, setF] = useState({ location: b?.location || row.location || "", at: b?.at || nowLocal(), note: b?.note || "", alternate_vehicle: b?.alternate_vehicle || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try { await api.post(`/tracking/market/${row.trip_id}/breakdown`, f); toast.success("Breakdown saved"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-card" data-testid="breakdown-dialog">
        <DialogHeader><DialogTitle className="text-red-600">{b ? "Edit Breakdown" : "Report Breakdown"} — {row.vehicle_number}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Breakdown Location *" className="sm:col-span-2"><Input data-testid="bd-location-input" value={f.location} onChange={set("location")} /></Field>
          <Field label="Date & Time *"><Input type="datetime-local" data-testid="bd-at-input" value={f.at} max={nowLocal()} onChange={set("at")} /></Field>
          <Field label="Alternate Vehicle No."><Input data-testid="bd-alt-input" className="uppercase" placeholder="If arranged" value={f.alternate_vehicle} onChange={set("alternate_vehicle")} /></Field>
          <Field label="Note" className="sm:col-span-2"><Input data-testid="bd-note-input" placeholder="Kya kharabi hai, kab tak theek hogi…" value={f.note} onChange={set("note")} /></Field>
        </div>
        <DialogFooter><Button variant="destructive" data-testid="bd-save-btn" onClick={save}>Save Breakdown</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
